import type { AdminTeamDto } from '@time-tracking/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useFocusTrap } from '../../hooks/useFocusTrap.js';

interface TeamPickerProps {
  anchorRect: DOMRect;
  count: number;
  teams: AdminTeamDto[];
  onPick: (teamId: string) => void;
  onClose: () => void;
}

export function TeamPicker({ anchorRect, count, teams, onPick, onClose }: TeamPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  useFocusTrap(ref, true);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    const timer = setTimeout(() => window.addEventListener('mousedown', onOutside), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(timer);
      window.removeEventListener('mousedown', onOutside);
    };
  }, [onClose]);

  const style = useMemo(() => {
    const top = anchorRect.bottom + 6;
    const right = Math.max(16, window.innerWidth - anchorRect.right);
    return { top, right };
  }, [anchorRect]);

  const q = query.trim().toLowerCase();
  const matches = (t: AdminTeamDto) =>
    !q ||
    t.teamName.toLowerCase().includes(q) ||
    (t.leadDisplayName?.toLowerCase() ?? '').includes(q);

  const filtered = teams.filter(matches);

  return (
    <div
      ref={ref}
      className="team-picker"
      style={{ position: 'fixed', zIndex: 80, ...style }}
      role="dialog"
      aria-label="Assign to team"
      aria-modal="true"
    >
      <div className="team-picker__head">
        <div className="team-picker__title">
          {count > 1 ? `Assign ${count} people` : 'Assign to team'}
        </div>
        <div className="team-picker__sub">Sheet will route to the team's lead going forward.</div>
        <div className="team-picker__search">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder={`Search ${teams.length} team${teams.length === 1 ? '' : 's'} by name or lead…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const first = filtered[0];
                if (first) onPick(first.teamId);
              }
            }}
          />
        </div>
      </div>
      <div className="team-picker__list">
        {filtered.map((t) => (
          <button
            key={t.teamId}
            type="button"
            className="team-pick-row"
            onClick={() => onPick(t.teamId)}
          >
            <div className="team-pick-row__who">
              <div className="team-pick-row__name">{t.teamName}</div>
              <div className="team-pick-row__meta">Lead: {t.leadDisplayName ?? '—'}</div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="team-picker__empty">
            {q ? `No teams match "${query}".` : 'No teams available.'}
          </div>
        )}
      </div>
      <div className="team-picker__foot">
        <Link to="/admin" onClick={onClose}>
          Create new team in Admin →
        </Link>
      </div>
    </div>
  );
}
